import { clamp } from 'lodash';
import React from 'react';

import { ListState } from './list-state';

interface GridCellOptions<T> {
  itemKey: string;
  listState: ListState<T>;
  ref: React.RefObject<HTMLElement>;
  enableAutoSelect?: boolean;
  onSelect?: (itemKey: string) => void;
  isDisabled?: boolean;
}

interface GridCellProps {
  cellProps: React.HTMLAttributes<HTMLElement>;
  isFocused: boolean;
  isSelected: boolean;
}

export function useAriaGridCell<T>(options: GridCellOptions<T>): GridCellProps {
  const { itemKey, listState, ref, enableAutoSelect, onSelect, isDisabled } = options;

  const isFocused = itemKey === listState.focusedKey;
  const isSelected = itemKey === listState.selectedKey;

  useGridCellAutoFocus(ref, isFocused);

  const triggerIndexChange = (delta: number) => {
    const keys = listState.getKeys();
    const currentIdx = keys.findIndex(key => key === itemKey);
    const newIdx = clamp(currentIdx + delta, 0, keys.length - 1);
    listState.setFocusedKey(keys[newIdx]);
    if (enableAutoSelect) {
      listState.setSelectedKey(keys[newIdx]);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (document.activeElement !== ref.current) {
      // focus is on child.
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      triggerIndexChange(-1);
      event.preventDefault();
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      triggerIndexChange(+1);
      event.preventDefault();
    }
    if (event.key === 'PageUp' || event.key === 'Home') {
      triggerIndexChange(-Infinity);
      event.preventDefault();
    }
    if (event.key === 'PageDown' || event.key === 'End') {
      triggerIndexChange(+Infinity);
      event.preventDefault();
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      listState.setSelectedKey(itemKey);
      if (onSelect) {
        onSelect(itemKey);
      }
    }
  };

  const onClick = () => {
    listState.setFocusedKey(itemKey);
    listState.setSelectedKey(itemKey);
    if (onSelect) {
      onSelect(itemKey);
    }
  };

  const cellProps = {
    'aria-selected': isSelected,
    role: 'gridcell',
    tabIndex: isDisabled ? -1 : 0,
    onClick,
    onKeyDown,
  };

  return {
    cellProps,
    isFocused,
    isSelected,
  };
}

interface AriaGridProps<T> {
  children: (item: T, idx: number) => React.ReactNode;
  listState: ListState<T>;
}

export const AriaGrid = <T,>({ children, listState, ...restProps }: AriaGridProps<T>) => {
  const keys = listState.getKeys();
  // Note: we don't need the row but aria does.
  const elements = listState.items.map((item, idx) => (
    <div key={keys[idx]} role="row">
      {children(item, idx)}
    </div>
  ));

  return (
    <div role="grid" {...restProps}>
      {elements}
    </div>
  );
};

function useGridCellAutoFocus(ref: React.RefObject<HTMLElement>, isFocused: boolean) {
  React.useLayoutEffect(() => {
    if (!isFocused) {
      return;
    }
    if (ref.current) {
      /**
       * Hack: check if the focus was within grid. This is not to steal focus
       * from map navigation.
       */
      if (document.activeElement?.getAttribute('role') === 'gridcell') {
        ref.current.focus();
      }
    }
  }, [isFocused]);
}
